import inflection from 'inflection';

// Pure helpers for turning validation failures - client-side (react-final-form)
// and server-side (the insights API's validation_error envelope) - into
// something a user can act on.
//
// NBU-9. The console's forms are TabbedForms, and react-admin v3 renders every
// tab and hides the inactive ones with `display: none`. A field that fails
// validation on a tab the user is not looking at is therefore mounted, marked
// invalid, and completely invisible: the only feedback was a toast that named
// no field and no tab.
//
// Everything here is pure and DOM-free apart from resolveFieldLabel, which
// takes its document as an argument. The React pieces that consume it live in
// ErrorAwareFormTabs.js and ValidatingSaveButton.js.

// react-final-form nests its errors object by source path, so a validator on
// `frequency_cap.cap` produces `{frequency_cap: {cap: <error>}}`. A LEAF is
// either a plain string or react-admin's ValidationError shape
// ({message, args} - see ra-core form/validate.js getMessage), which is itself
// an object and would otherwise be walked into as if it were a nested source.
const isLeafError = (value) =>
  typeof value === 'string' ||
  (value != null && typeof value === 'object' && typeof value.message === 'string');

/**
 * Flatten react-final-form's nested errors object into the list of source paths
 * that actually failed.
 *
 * @param {object} errors form.getState().errors
 * @param {string} [prefix] internal - the source path walked so far
 * @returns {string[]} e.g. ['bid_price', 'frequency_cap.cap']
 */
export function flattenErrorSources (errors, prefix = '') {
  if (errors == null || typeof errors !== 'object') return [];
  const sources = [];
  const entries = Array.isArray(errors)
    // Array fields (SimpleFormIterator rows, array-valued inputs) key their
    // errors by index, and final-form addresses those rows as `source[0]`.
    ? errors.map((value, index) => [`${prefix}[${index}]`, value, true])
    : Object.entries(errors).map(([key, value]) => [prefix ? `${prefix}.${key}` : key, value, false]);

  for (const [path, value, isIndexed] of entries) {
    if (value == null) continue;
    if (isLeafError(value)) {
      // An array whose error is attached to the array itself (a `required()` on
      // the input, not on a row) reports the array's own source, not `[0]`.
      sources.push(isIndexed ? prefix : path);
      continue;
    }
    if (typeof value === 'object') sources.push(...flattenErrorSources(value, path));
  }
  // An array of leaf errors pushes the same parent path once per row.
  return sources.filter((source, index) => sources.indexOf(source) === index);
}

/**
 * Best-effort human name for a source path, used only when the live label
 * cannot be read off the form (see resolveFieldLabel).
 *
 * @param {string} source e.g. 'frequency_cap.cap'
 * @returns {string} e.g. 'Cap'
 */
export function humanizeSource (source) {
  if (!source) return '';
  const leaf = String(source).split('.').pop().replace(/\[\d+\]$/, '');
  if (!leaf) return String(source);
  return inflection.humanize(inflection.underscore(leaf));
}

/**
 * The label the user can actually SEE for a field, read from the rendered form.
 *
 * react-admin's useInput defaults an input's `id` to its `source`, and
 * Material-UI wires the visible <label> to it with `for`. Reading that back
 * gives the exact on-screen wording - "Bid Price (USD CPM)", not a guess
 * derived from the field name - which matters because naming a field by a label
 * that appears nowhere on screen is worse than naming no field at all.
 *
 * Hidden tabs are MOUNTED (react-admin hides them with `display: none`), so the
 * labels of fields on other tabs are readable too - which is the whole point.
 *
 * Falls back to humanizeSource when there is no matching input: server-side
 * details name paths like `segment.filter_ip_list.values` that no single input
 * owns.
 *
 * @param {string} source
 * @param {Document} [doc]
 * @returns {string}
 */
export function resolveFieldLabel (source, doc = typeof document === 'undefined' ? null : document) {
  const fallback = humanizeSource(source);
  if (!doc || !source) return fallback;
  // getElementById, not querySelector: source paths contain dots, which are
  // class selectors in CSS and would need escaping.
  const input = doc.getElementById(source);
  if (!input) return fallback;
  const labels = input.labels && input.labels.length
    ? Array.from(input.labels)
    : Array.from(doc.querySelectorAll('label')).filter(el => el.htmlFor === source);
  const text = labels.length ? (labels[0].textContent || '').trim() : '';
  // Material-UI appends " *" to a required field's label; the asterisk is
  // decoration, not part of the name.
  const cleaned = text.replace(/\s*\*$/, '').trim();
  return cleaned || fallback;
}

/**
 * The message shown when a save is blocked by client-side validation.
 *
 * Deliberately says "highlighted tabs" rather than naming them: the tab badges
 * are the durable pointer, and copy that names a tab goes stale silently when
 * the tabs are renamed.
 *
 * @param {string[]} sources
 * @param {Document} [doc]
 * @returns {string|null} null when there is nothing to report
 */
export function describeInvalidForm (sources, doc) {
  if (!Array.isArray(sources) || sources.length === 0) return null;
  const names = sources.map(source => resolveFieldLabel(source, doc));
  const count = names.length;
  const noun = count === 1 ? 'field needs' : 'fields need';
  return `${count} ${noun} attention: ${names.join(', ')}. Look for the tabs marked with a warning.`;
}

/**
 * Pull the per-field details out of an insights validation_error response.
 *
 * The API sends `{error: {code, message, details: [{field, message}]}, message}`
 * (insights routes/api/v1/errors.js). react-admin's fetchJson keeps the parsed
 * body on `HttpError.body`, but its default onFailure reads only the top-level
 * `message` - which for this envelope is the useless summary count
 * ("1 field failed validation"), the exact string reported in NBU-9.
 *
 * @param {Error} error the HttpError react-admin threw
 * @returns {Array<{field: string, message: string}>} [] when this is not a validation error
 */
export function serverValidationDetails (error) {
  const body = error && error.body;
  const details = body && body.error && body.error.details;
  if (!Array.isArray(details)) return [];
  return details.filter(detail =>
    detail && typeof detail === 'object' && typeof detail.message === 'string');
}

/**
 * The message shown when the SERVER rejects a save.
 *
 * @param {Error} error
 * @param {Document} [doc]
 * @returns {string|null} null when the error carries no per-field details, so
 *   the caller can fall back to the error's own message
 */
export function describeServerValidation (error, doc) {
  const details = serverValidationDetails(error);
  if (details.length === 0) return null;
  return details
    .map(detail => detail.field
      ? `${resolveFieldLabel(detail.field, doc)}: ${detail.message}`
      : detail.message)
    .join(' | ');
}

/**
 * The route a FormTab is addressed by, and therefore also the name of the form
 * group react-admin registers its inputs under.
 *
 * Reimplemented rather than imported: react-admin exports TabbedFormTabs from
 * its public entry point but NOT getTabFullPath, so importing it would mean
 * reaching into ra-ui-materialui's internal file layout. formErrors.test.js
 * asserts this stays byte-identical to react-admin's own implementation.
 *
 * @param {ReactElement} tab
 * @param {number} index
 * @param {string} baseUrl
 * @returns {string}
 */
export function tabFullPath (tab, index, baseUrl) {
  const suffix = (tab && tab.props && tab.props.path)
    ? `/${tab.props.path}`
    : index > 0 ? `/${index}` : '';
  return `${baseUrl}${suffix}`.replace('//', '/');
}
